import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { appRegistry } from "./appRegistry.ts";
import { AppWidget } from "./appWidget.tsx";
import { ConfigureDialog } from "./configureDialog.tsx";
import { InstantDashboardPage } from "../instantDashboard/instantDashboardPage.tsx";
import { setStartPagePreference } from "../../components/nav/userMenu.tsx";
import styles from "./classicDashboard.module.scss";

/**
 * Classic dashboard page — configurable grid of app widgets.
 *
 * Reads `cache.UserApps` (populated from the backend AppDataArr) and
 * renders each configured app using the registry-driven AppWidget.
 * Falls back to a sensible default set when the user has no apps configured.
 */

const DEFAULT_APPS = [
  { AppName: "KioskStatusApp", Row: 0, Col: 0 },
  { AppName: "TransactionRateApp", Row: 0, Col: 1 },
  { AppName: "GlobalAnnouncementApp", Row: 1, Col: 0 },
  { AppName: "ProductSalesApp", Row: 1, Col: 1 },
];

interface UserApp {
  AppName?: string;
  Row?: number;
  Col?: number;
  [key: string]: unknown;
}

/** Endpoint IDs that redirect to an instantDashboard variant instead of the widget dashboard. */
export const ENDPOINT_DASHBOARD_MAP: Record<number, string> = {
  26: "instantdashboardglobal",
  57: "instantdashboards",
  87: "instantdashboardcg",
  102: "instantdashboardrg",
  118: "instantdashboardcantaloupe",
  126: "instantdashboardcantaloupea",
  127: "instantdashboardcantaloupeb",
};

export function ClassicDashboardPage() {
  const { session, cache } = useAuth();
  const navigate = useNavigate();
  const [configOpen, setConfigOpen] = useState(false);

  const switchToCockpit = useCallback(() => {
    setStartPagePreference(session?.UID, session?.ActiveEndPointID, "new");
    navigate("/");
    setTimeout(() => window.location.reload(), 0);
  }, [navigate, session?.UID, session?.ActiveEndPointID]);

  // Redirect specific endpoints to their branded instantDashboard
  const overridePage = session?.ActiveEndPointID
    ? ENDPOINT_DASHBOARD_MAP[session.ActiveEndPointID]
    : undefined;
  if (overridePage) {
    return <InstantDashboardPage pageName={overridePage} />;
  }

  const { apps, columns } = useMemo(() => {
    const raw = (cache?.UserApps as UserApp[] | undefined) ?? [];
    const list = raw.length > 0 ? raw : DEFAULT_APPS;
    // Derive column count from max Col value + 1 (backend stores 0-based cols)
    const maxCol = list.reduce((m, a) => Math.max(m, a.Col ?? 0), 0);
    const columns = Math.max(1, maxCol + 1);
    const sorted = [...list]
      .filter((a) => a.AppName && appRegistry[a.AppName])
      .sort((a, b) => (a.Row ?? 0) - (b.Row ?? 0) || (a.Col ?? 0) - (b.Col ?? 0));
    return { apps: sorted, columns };
  }, [cache?.UserApps]);

  const handleCloseConfig = useCallback(() => setConfigOpen(false), []);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <span className={styles.lastUpdated}>
          Dashboard — {apps.length} widget{apps.length !== 1 ? "s" : ""}
        </span>
        <button className={styles.switchBtn} onClick={switchToCockpit} title="Switch to Cockpit dashboard">
          Switch to Cockpit (new) →
        </button>
        <button className={styles.configureBtn} onClick={() => setConfigOpen(true)}>
          Configure
        </button>
      </div>

      {apps.length === 0 ? (
        <p className={styles.appLoading}>No dashboard apps configured.</p>
      ) : (
        <div className={styles.appGrid} style={{ "--dashboard-columns": columns } as React.CSSProperties}>
          {apps.map((app, i) => {
            const name = app.AppName!;
            return <AppWidget key={`${name}-${i}`} appName={name} config={appRegistry[name]!} />;
          })}
        </div>
      )}

      {configOpen && <ConfigureDialog onClose={handleCloseConfig} />}
    </div>
  );
}
